import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Button from '../../components/Button';
import PageLoader from '../../components/PageLoader';
import { colors } from '../../config/colors';
import { svg } from '../../config/images';
import { getBrokersRaw } from '../../api';

const DASH = '-';

const toTextOrDash = (v) => {
  if (v == null) return DASH;
  if (typeof v === 'string') { const s = v.trim(); return s ? s : DASH; }
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : DASH;
  return DASH;
};

const toNumberOrNull = (v) => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const percentToWidth = (v) => {
  const s = typeof v === 'string' ? (v || '').trim() : '';
  if (!s || s === DASH) return '0%';
  if (s.endsWith('%')) return s;
  const n = Number(s);
  return Number.isFinite(n) ? `${Math.min(100, Math.max(0, n))}%` : '0%';
};

const CompareBrokers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const broker1Name = searchParams.get('broker1') || location.state?.broker1 || '';
  const broker2Name = searchParams.get('broker2') || location.state?.broker2 || '';

  const [allBrokers, setAllBrokers] = useState([]);
  const [broker1, setBroker1] = useState(null);
  const [broker2, setBroker2] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDropdown1, setShowDropdown1] = useState(false);
  const [showDropdown2, setShowDropdown2] = useState(false);
  const [activeSection, setActiveSection] = useState('activeClients');
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await getBrokersRaw();
        if (!mounted) return;
        setAllBrokers(Array.isArray(list) ? list : []);
        const arr = Array.isArray(list) ? list : [];
        const b1 = arr.find((b) => b && (b.name === broker1Name || b.slug === broker1Name)) || arr[0];
        const b2 = arr.find((b) => b && (b.name === broker2Name || b.slug === broker2Name)) || arr[1] || arr[0];
        setBroker1(b1 || null);
        setBroker2(b2 || null);
      } catch {
        if (mounted) setAllBrokers([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [broker1Name, broker2Name]);

  // Refs for smooth scrolling
  const sectionRefs = {
    activeClients: useRef(null),
    charges: useRef(null),
    complaints: useRef(null),
    shareHolding: useRef(null),
    prosCons: useRef(null),
    financials: useRef(null),
    ratings: useRef(null),
  };

  const getDetails = (b) => {
    if (!b) {
      return {
        rating: null,
        activeClients: DASH,
        activeClientsTrend: DASH,
        charges: { delivery: DASH, intraday: DASH, futures: DASH },
        complaints: { total: null, resolved: null },
        shareHolding: { promoters: DASH, public: DASH },
        financials: { revenue: DASH, profit: DASH, marketCap: DASH },
        pros: [],
        cons: [],
      };
    }
    return {
      rating: toNumberOrNull(b.rating),
      activeClients: toTextOrDash(b.activeClients),
      activeClientsTrend: toTextOrDash(b.activeClientsTrend),
      charges: {
        delivery: toTextOrDash(b.charges?.delivery),
        intraday: toTextOrDash(b.charges?.intraday),
        futures: toTextOrDash(b.charges?.futures),
      },
      complaints: {
        total: toNumberOrNull(b.complaints?.total),
        resolved: toNumberOrNull(b.complaints?.resolved),
      },
      shareHolding: {
        promoters: toTextOrDash(b.shareHolding?.promoters),
        public: toTextOrDash(b.shareHolding?.public),
      },
      financials: {
        revenue: toTextOrDash(b.financials?.revenue),
        profit: toTextOrDash(b.financials?.profit),
        marketCap: toTextOrDash(b.financials?.marketCap),
      },
      pros: Array.isArray(b.pros) ? b.pros.filter(Boolean) : [],
      cons: Array.isArray(b.cons) ? b.cons.filter(Boolean) : [],
    };
  };

  const d1 = getDetails(broker1);
  const d2 = getDetails(broker2);
  const comparisonData = {
    activeClients: {
      broker1: { value: d1.activeClients, trend: d1.activeClientsTrend === 'Declining' ? 'down' : d1.activeClientsTrend === 'Stable' ? 'flat' : 'up' },
      broker2: { value: d2.activeClients, trend: d2.activeClientsTrend === 'Declining' ? 'down' : d2.activeClientsTrend === 'Stable' ? 'flat' : 'up' },
    },
    charges: { broker1: d1.charges, broker2: d2.charges },
    complaints: { broker1: d1.complaints, broker2: d2.complaints },
    shareHolding: { broker1: d1.shareHolding, broker2: d2.shareHolding },
    financials: { broker1: d1.financials, broker2: d2.financials },
    ratings: { broker1: d1.rating, broker2: d2.rating },
    pros: { broker1: d1.pros, broker2: d2.pros },
    cons: { broker1: d1.cons, broker2: d2.cons },
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.broker-dropdown')) {
        setShowDropdown1(false);
        setShowDropdown2(false);
      }
    };

    if (showDropdown1 || showDropdown2) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown1, showDropdown2]);

  // Scroll to section
  const scrollToSection = (sectionId) => {
    const section = sectionRefs[sectionId]?.current;
    if (section) {
      setIsScrolling(true);
      setActiveSection(sectionId);

      const headerOffset = 120; // Account for sticky header
      const elementPosition = section.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: Math.max(0, offsetPosition),
        behavior: 'smooth'
      });

      // Re-enable scroll tracking after scroll completes
      setTimeout(() => {
        setIsScrolling(false);
      }, 1000);
    }
  };

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      // Don't update active section if we're programmatically scrolling
      if (isScrolling) return;

      const scrollPosition = window.scrollY + 200; // Offset for header

      // Check each section to see which one is currently in view
      let currentSection = null;
      for (const [sectionId, ref] of Object.entries(sectionRefs)) {
        if (ref.current) {
          const { offsetTop, offsetHeight } = ref.current;
          const sectionTop = offsetTop;
          const sectionBottom = offsetTop + offsetHeight;

          if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
            currentSection = sectionId;
            break;
          }
        }
      }

      // Also check if we're at the top of the page
      if (window.scrollY < 100) {
        currentSection = 'activeClients';
      }

      // Only update if we found a section and it's different
      if (currentSection && currentSection !== activeSection) {
        setActiveSection(currentSection);
      }
    };

    // Initial check
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeSection, isScrolling]);

  const sidebarItems = [
    { id: 'activeClients', label: 'Active Clients', icon: '🤝' },
    { id: 'charges', label: 'Charges', icon: '💰' },
    { id: 'complaints', label: 'Complaints', icon: '⚠️' },
    { id: 'shareHolding', label: 'Share Holding', icon: '📊' },
    { id: 'prosCons', label: 'Pros & Cons', icon: '⚖️' },
    { id: 'financials', label: 'Financials', icon: '📄' },
    { id: 'ratings', label: 'Ratings', icon: '⭐' },
  ];

  const slugify = (name = '') =>
    String(name).trim().toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1">
        <div className="max-w-7xl 4xl:max-w-10xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {loading && <PageLoader />}
          {!loading && (!broker1 || !broker2) && (
            <div className="text-center py-12 text-gray-600">No brokers available to compare.</div>
          )}
          {!loading && broker1 && broker2 && (
            <>
              {/* Page Header */}
              <div className="mb-8">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  Compare Brokers
                </h1>
                <p className="text-gray-600">Compare {broker1.name} vs {broker2.name} side by side</p>
              </div>

              {/* Broker Selection Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {/* Broker 1 Card */}
                <div className="relative broker-dropdown">
                  <div className="bg-white rounded-xl p-6 border-2 border-gray-200 shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 bg-gradient-to-br ${broker1.color || 'from-primary-400 to-primary-600'} rounded-lg flex items-center justify-center text-white text-2xl`}>
                          {broker1.logo || '📊'}
                        </div>
                        <span className="font-bold text-gray-900 text-lg">{broker1.name}</span>
                      </div>
                      <button
                        onClick={() => {
                          setShowDropdown1(!showDropdown1);
                          setShowDropdown2(false);
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <img
                          src={svg.chevronDown}
                          alt="dropdown"
                          className={`w-5 h-5 transition-transform duration-300 ${showDropdown1 ? 'rotate-180' : ''}`}
                          style={{ filter: 'brightness(0) saturate(100%) invert(40%) sepia(8%) saturate(1270%) hue-rotate(182deg) brightness(95%) contrast(86%)' }}
                        />
                      </button>
                    </div>

                    {/* Rating */}
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            className={`w-4 h-4 ${i < Math.floor(comparisonData.ratings.broker1 ?? 0) ? 'text-yellow-400' : 'text-gray-300'}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <span className="text-sm text-gray-600">({comparisonData.ratings.broker1 != null ? `${comparisonData.ratings.broker1}/5.0` : DASH} ratings)</span>
                    </div>

                    <div className="flex flex-col gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="w-full"
                        onClick={() => navigate(`/broker/${broker1.slug || slugify(broker1.name)}`)}
                      >
                        View Details
                      </Button>
                      <Button variant="primary" className="w-full">
                        Open Demat Account
                      </Button>
                    </div>

                    {/* Dropdown */}
                    {showDropdown1 && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-2xl z-30 max-h-48 overflow-y-auto">
                        {allBrokers
                          .filter(b => b.name !== broker2.name)
                          .map((broker) => (
                            <button
                              key={broker._id || broker.id || broker.name}
                              onClick={() => {
                                setBroker1(broker);
                                setShowDropdown1(false);
                                navigate(`/compare?broker1=${broker.name}&broker2=${broker2.name}`);
                              }}
                              className="w-full p-3 flex items-center gap-3 hover:bg-gradient-to-r hover:from-primary-50 hover:to-secondary-50 transition-all duration-200 first:rounded-t-xl last:rounded-b-xl border-b border-gray-100 last:border-0"
                            >
                              <div className={`w-10 h-10 bg-gradient-to-br ${broker.color || 'from-primary-400 to-primary-600'} rounded-lg flex items-center justify-center text-white text-lg shadow-md flex-shrink-0`}>
                                {broker.logo || '📊'}
                              </div>
                              <span className="font-semibold text-gray-900 text-sm">{broker.name}</span>
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* VS Divider */}
                <div className="flex flex-col items-center justify-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-500 rounded-full flex items-center justify-center shadow-2xl">
                    <span className="text-white font-bold text-xl">VS</span>
                  </div>
                </div>

                {/* Broker 2 Card */}
                <div className="relative broker-dropdown">
                  <div className="bg-white rounded-xl p-6 border-2 border-primary-300 shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 bg-gradient-to-br ${broker2.color || 'from-primary-400 to-primary-600'} rounded-lg flex items-center justify-center text-white text-2xl`}>
                          {broker2.logo || '📊'}
                        </div>
                        <span className="font-bold text-gray-900 text-lg">{broker2.name}</span>
                      </div>
                      <button
                        onClick={() => {
                          setShowDropdown2(!showDropdown2);
                          setShowDropdown1(false);
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <img
                          src={svg.chevronDown}
                          alt="dropdown"
                          className={`w-5 h-5 transition-transform duration-300 ${showDropdown2 ? 'rotate-180' : ''}`}
                          style={{ filter: 'brightness(0) saturate(100%) invert(40%) sepia(8%) saturate(1270%) hue-rotate(182deg) brightness(95%) contrast(86%)' }}
                        />
                      </button>
                    </div>

                    {/* Rating */}
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            className={`w-4 h-4 ${i < Math.floor(comparisonData.ratings.broker2 ?? 0) ? 'text-yellow-400' : 'text-gray-300'}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <span className="text-sm text-gray-600">({comparisonData.ratings.broker2 != null ? `${comparisonData.ratings.broker2}/5.0` : DASH} ratings)</span>
                    </div>

                    <div className="flex flex-col gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="w-full"
                        onClick={() => navigate(`/broker/${broker2.slug || slugify(broker2.name)}`)}
                      >
                        View Details
                      </Button>
                      <Button variant="primary" className="w-full">
                        Open Demat Account
                      </Button>
                    </div>

                    {/* Dropdown */}
                    {showDropdown2 && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-2xl z-30 max-h-48 overflow-y-auto">
                        {allBrokers
                          .filter(b => b.name !== broker1.name)
                          .map((broker) => (
                            <button
                              key={broker._id || broker.id || broker.name}
                              onClick={() => {
                                setBroker2(broker);
                                setShowDropdown2(false);
                                navigate(`/compare?broker1=${broker1.name}&broker2=${broker.name}`);
                              }}
                              className="w-full p-3 flex items-center gap-3 hover:bg-gradient-to-r hover:from-primary-50 hover:to-secondary-50 transition-all duration-200 first:rounded-t-xl last:rounded-b-xl border-b border-gray-100 last:border-0"
                            >
                              <div className={`w-10 h-10 bg-gradient-to-br ${broker.color || 'from-primary-400 to-primary-600'} rounded-lg flex items-center justify-center text-white text-lg shadow-md flex-shrink-0`}>
                                {broker.logo || '📊'}
                              </div>
                              <span className="font-semibold text-gray-900 text-sm">{broker.name}</span>
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Comparison Content - All Sections on One Page */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Sidebar Navigation */}
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-lg sticky top-24">
                    <div className="space-y-2">
                      {sidebarItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => scrollToSection(item.id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200 text-left ${activeSection === item.id
                            ? 'bg-primary-500 text-white shadow-md'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                          <span className="text-xl">{item.icon}</span>
                          <span className="font-semibold text-sm">{item.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Main Content - All Sections */}
                <div className="lg:col-span-3 space-y-8">
                  {/* Active Clients Section */}
                  <section id="activeClients" ref={sectionRefs.activeClients} className="scroll-mt-24">
                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                        <span>🤝</span>
                        Active Clients
                      </h2>

                      <div className="space-y-6">
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-4">Active Users Growth</h3>
                          <div className="h-64 relative">
                            <svg viewBox="0 0 400 200" className="w-full h-full">
                              {/* Y-axis labels */}
                              <text x="10" y="30" className="text-xs fill-gray-600">90L</text>
                              <text x="10" y="60" className="text-xs fill-gray-600">70L</text>
                              <text x="10" y="90" className="text-xs fill-gray-600">50L</text>
                              <text x="10" y="120" className="text-xs fill-gray-600">30L</text>
                              <text x="10" y="150" className="text-xs fill-gray-600">20L</text>

                              {/* Grid lines */}
                              <line x1="40" y1="20" x2="40" y2="180" stroke={colors.gray[200]} strokeWidth="1" />
                              <line x1="40" y1="40" x2="380" y2="40" stroke={colors.gray[200]} strokeWidth="1" />
                              <line x1="40" y1="80" x2="380" y2="80" stroke={colors.gray[200]} strokeWidth="1" />
                              <line x1="40" y1="120" x2="380" y2="120" stroke={colors.gray[200]} strokeWidth="1" />
                              <line x1="40" y1="160" x2="380" y2="160" stroke={colors.gray[200]} strokeWidth="1" />

                              {/* Zerodha line (blue) */}
                              <polyline
                                points="60,140 120,100 180,70 240,50 300,40 360,35"
                                fill="none"
                                stroke="#3b82f6"
                                strokeWidth="3"
                              />
                              {/* Zerodha points */}
                              {[60, 120, 180, 240, 300, 360].map((x, i) => {
                                const y = 140 - (i * 20);
                                return (
                                  <circle key={i} cx={x} cy={y} r="4" fill="#3b82f6" />
                                );
                              })}

                              {/* Angel One line (orange) */}
                              <polyline
                                points="60,150 120,130 180,110 240,85 300,60 360,45"
                                fill="none"
                                stroke="#f97316"
                                strokeWidth="3"
                              />
                              {/* Angel One points */}
                              {[60, 120, 180, 240, 300, 360].map((x, i) => {
                                const y = 150 - (i * 20);
                                return (
                                  <circle key={i} cx={x} cy={y} r="4" fill="#f97316" />
                                );
                              })}
                            </svg>
                            {/* Legend */}
                            <div className="absolute bottom-0 right-0 flex gap-4">
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                                <span className="text-sm text-gray-700">{broker2.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                                <span className="text-sm text-gray-700">{broker1.name}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-4 border border-primary-200">
                            <div className="text-sm text-gray-600 mb-1">Active Clients</div>
                            <div className="text-2xl font-bold text-primary-700">{comparisonData.activeClients.broker1.value}</div>
                            <div className={`text-xs mt-1 ${comparisonData.activeClients.broker1.trend === 'down' ? 'text-red-600' : comparisonData.activeClients.broker1.trend === 'up' ? 'text-green-600' : 'text-gray-600'}`}>
                              {d1.activeClientsTrend === DASH ? DASH : comparisonData.activeClients.broker1.trend === 'down' ? '↓ Declining' : comparisonData.activeClients.broker1.trend === 'up' ? '↑ Growing' : '→ Stable'}
                            </div>
                          </div>
                          <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-4 border border-primary-200">
                            <div className="text-sm text-gray-600 mb-1">Active Clients</div>
                            <div className="text-2xl font-bold text-primary-700">{comparisonData.activeClients.broker2.value}</div>
                            <div className={`text-xs mt-1 ${comparisonData.activeClients.broker2.trend === 'down' ? 'text-red-600' : comparisonData.activeClients.broker2.trend === 'up' ? 'text-green-600' : 'text-gray-600'}`}>
                              {d2.activeClientsTrend === DASH ? DASH : comparisonData.activeClients.broker2.trend === 'down' ? '↓ Declining' : comparisonData.activeClients.broker2.trend === 'up' ? '↑ Growing' : '→ Stable'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Charges Section */}
                  <section id="charges" ref={sectionRefs.charges} className="scroll-mt-24">
                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                        <span>💰</span>
                        Charges
                      </h2>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-bold text-gray-900 mb-4">{broker1.name}</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-700">Delivery</span>
                              <span className="font-semibold text-gray-900">{comparisonData.charges.broker1.delivery}</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-700">Intraday</span>
                              <span className="font-semibold text-gray-900">{comparisonData.charges.broker1.intraday}</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-700">Futures</span>
                              <span className="font-semibold text-gray-900">{comparisonData.charges.broker1.futures}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 mb-4">{broker2.name}</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-700">Delivery</span>
                              <span className="font-semibold text-gray-900">{comparisonData.charges.broker2.delivery}</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-700">Intraday</span>
                              <span className="font-semibold text-gray-900">{comparisonData.charges.broker2.intraday}</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-700">Futures</span>
                              <span className="font-semibold text-gray-900">{comparisonData.charges.broker2.futures}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Complaints Section */}
                  <section id="complaints" ref={sectionRefs.complaints} className="scroll-mt-24">
                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                        <span>⚠️</span>
                        Complaints
                      </h2>
                      <div className="grid grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                          <h4 className="font-bold text-gray-900 mb-4">{broker1.name}</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-700">Total Complaints</span>
                              <span className="font-bold text-gray-900">{comparisonData.complaints.broker1.total != null ? comparisonData.complaints.broker1.total : DASH}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-700">Resolved</span>
                              <span className="font-bold text-green-600">{comparisonData.complaints.broker1.resolved != null ? comparisonData.complaints.broker1.resolved : DASH}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                              <div
                                className="bg-green-500 h-2 rounded-full"
                                style={{ width: comparisonData.complaints.broker1.total > 0 ? `${((comparisonData.complaints.broker1.resolved ?? 0) / comparisonData.complaints.broker1.total) * 100}%` : '0%' }}
                              ></div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                          <h4 className="font-bold text-gray-900 mb-4">{broker2.name}</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-700">Total Complaints</span>
                              <span className="font-bold text-gray-900">{comparisonData.complaints.broker2.total != null ? comparisonData.complaints.broker2.total : DASH}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-700">Resolved</span>
                              <span className="font-bold text-green-600">{comparisonData.complaints.broker2.resolved != null ? comparisonData.complaints.broker2.resolved : DASH}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                              <div
                                className="bg-green-500 h-2 rounded-full"
                                style={{ width: comparisonData.complaints.broker2.total > 0 ? `${((comparisonData.complaints.broker2.resolved ?? 0) / comparisonData.complaints.broker2.total) * 100}%` : '0%' }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Share Holding Section */}
                  <section id="shareHolding" ref={sectionRefs.shareHolding} className="scroll-mt-24">
                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                        <span>📊</span>
                        Share Holding
                      </h2>
                      <div className="grid grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                          <h4 className="font-bold text-gray-900 mb-4">{broker1.name}</h4>
                          <div className="space-y-4">
                            <div>
                              <div className="flex justify-between mb-2">
                                <span className="text-sm text-gray-700">Promoters</span>
                                <span className="font-bold text-gray-900">{comparisonData.shareHolding.broker1.promoters}</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3">
                                <div className="bg-primary-500 h-3 rounded-full" style={{ width: percentToWidth(comparisonData.shareHolding.broker1.promoters) }}></div>
                              </div>
                            </div>
                            <div>
                              <div className="flex justify-between mb-2">
                                <span className="text-sm text-gray-700">Public</span>
                                <span className="font-bold text-gray-900">{comparisonData.shareHolding.broker1.public}</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3">
                                <div className="bg-secondary-500 h-3 rounded-full" style={{ width: percentToWidth(comparisonData.shareHolding.broker1.public) }}></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                          <h4 className="font-bold text-gray-900 mb-4">{broker2.name}</h4>
                          <div className="space-y-4">
                            <div>
                              <div className="flex justify-between mb-2">
                                <span className="text-sm text-gray-700">Promoters</span>
                                <span className="font-bold text-gray-900">{comparisonData.shareHolding.broker2.promoters}</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3">
                                <div className="bg-primary-500 h-3 rounded-full" style={{ width: percentToWidth(comparisonData.shareHolding.broker2.promoters) }}></div>
                              </div>
                            </div>
                            <div>
                              <div className="flex justify-between mb-2">
                                <span className="text-sm text-gray-700">Public</span>
                                <span className="font-bold text-gray-900">{comparisonData.shareHolding.broker2.public}</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3">
                                <div className="bg-secondary-500 h-3 rounded-full" style={{ width: percentToWidth(comparisonData.shareHolding.broker2.public) }}></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Pros & Cons Section */}
                  <section id="prosCons" ref={sectionRefs.prosCons} className="scroll-mt-24">
                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                        <span>⚖️</span>
                        Pros & Cons
                      </h2>
                      <div className="grid grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                          <h4 className="font-bold text-gray-900 mb-4">{broker1.name}</h4>
                          <div className="space-y-4">
                            <div>
                              <h5 className="font-semibold text-green-600 mb-2">Pros</h5>
                              <ul className="space-y-2 text-sm text-gray-700">
                                {comparisonData.pros.broker1.length ? comparisonData.pros.broker1.map((item, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <span className="text-green-500 mt-1">✓</span>
                                    <span>{item}</span>
                                  </li>
                                )) : (
                                  <li className="flex items-start gap-2"><span className="text-gray-400">—</span></li>
                                )}
                              </ul>
                            </div>
                            <div>
                              <h5 className="font-semibold text-red-600 mb-2">Cons</h5>
                              <ul className="space-y-2 text-sm text-gray-700">
                                {comparisonData.cons.broker1.length ? comparisonData.cons.broker1.map((item, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <span className="text-red-500 mt-1">✗</span>
                                    <span>{item}</span>
                                  </li>
                                )) : (
                                  <li className="flex items-start gap-2"><span className="text-gray-400">—</span></li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                          <h4 className="font-bold text-gray-900 mb-4">{broker2.name}</h4>
                          <div className="space-y-4">
                            <div>
                              <h5 className="font-semibold text-green-600 mb-2">Pros</h5>
                              <ul className="space-y-2 text-sm text-gray-700">
                                {comparisonData.pros.broker2.length ? comparisonData.pros.broker2.map((item, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <span className="text-green-500 mt-1">✓</span>
                                    <span>{item}</span>
                                  </li>
                                )) : (
                                  <li className="flex items-start gap-2"><span className="text-gray-400">—</span></li>
                                )}
                              </ul>
                            </div>
                            <div>
                              <h5 className="font-semibold text-red-600 mb-2">Cons</h5>
                              <ul className="space-y-2 text-sm text-gray-700">
                                {comparisonData.cons.broker2.length ? comparisonData.cons.broker2.map((item, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <span className="text-red-500 mt-1">✗</span>
                                    <span>{item}</span>
                                  </li>
                                )) : (
                                  <li className="flex items-start gap-2"><span className="text-gray-400">—</span></li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Financials Section */}
                  <section id="financials" ref={sectionRefs.financials} className="scroll-mt-24">
                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                        <span>📄</span>
                        Financials
                      </h2>
                      <div className="grid grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                          <h4 className="font-bold text-gray-900 mb-4">{broker1.name}</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between p-3 bg-white rounded-lg border border-gray-200">
                              <span className="text-sm text-gray-700">Revenue</span>
                              <span className="font-semibold text-gray-900">{comparisonData.financials.broker1.revenue}</span>
                            </div>
                            <div className="flex justify-between p-3 bg-white rounded-lg border border-gray-200">
                              <span className="text-sm text-gray-700">Profit</span>
                              <span className="font-semibold text-gray-900">{comparisonData.financials.broker1.profit}</span>
                            </div>
                            <div className="flex justify-between p-3 bg-white rounded-lg border border-gray-200">
                              <span className="text-sm text-gray-700">Market Cap</span>
                              <span className="font-semibold text-gray-900">{comparisonData.financials.broker1.marketCap}</span>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                          <h4 className="font-bold text-gray-900 mb-4">{broker2.name}</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between p-3 bg-white rounded-lg border border-gray-200">
                              <span className="text-sm text-gray-700">Revenue</span>
                              <span className="font-semibold text-gray-900">{comparisonData.financials.broker2.revenue}</span>
                            </div>
                            <div className="flex justify-between p-3 bg-white rounded-lg border border-gray-200">
                              <span className="text-sm text-gray-700">Profit</span>
                              <span className="font-semibold text-gray-900">{comparisonData.financials.broker2.profit}</span>
                            </div>
                            <div className="flex justify-between p-3 bg-white rounded-lg border border-gray-200">
                              <span className="text-sm text-gray-700">Market Cap</span>
                              <span className="font-semibold text-gray-900">{comparisonData.financials.broker2.marketCap}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Ratings Section */}
                  <section id="ratings" ref={sectionRefs.ratings} className="scroll-mt-24">
                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                        <span>⭐</span>
                        Ratings
                      </h2>
                      <div className="grid grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center border border-primary-200">
                          <h4 className="font-bold text-gray-900 mb-4">{broker1.name}</h4>
                          <div className="flex justify-center gap-1 mb-2">
                            {[...Array(5)].map((_, i) => (
                              <svg
                                key={i}
                                className={`w-6 h-6 ${i < Math.floor(comparisonData.ratings.broker1 ?? 0) ? 'text-yellow-400' : 'text-gray-300'}`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                          <div className="text-2xl font-bold text-gray-900">
                            {comparisonData.ratings.broker1 != null ? `${comparisonData.ratings.broker1}/5.0` : DASH}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">ratings</div>
                        </div>
                        <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center border border-primary-200">
                          <h4 className="font-bold text-gray-900 mb-4">{broker2.name}</h4>
                          <div className="flex justify-center gap-1 mb-2">
                            {[...Array(5)].map((_, i) => (
                              <svg
                                key={i}
                                className={`w-6 h-6 ${i < Math.floor(comparisonData.ratings.broker2 ?? 0) ? 'text-yellow-400' : 'text-gray-300'}`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                          <div className="text-2xl font-bold text-gray-900">
                            {comparisonData.ratings.broker2 != null ? `${comparisonData.ratings.broker2}/5.0` : DASH}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">ratings</div>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CompareBrokers;
